'use client';

import { useTranslation } from '@/i18n/LanguageProvider';
import { useEffect, useState, useRef, lazy, Suspense } from 'react';
import Link from '@/components/LocaleLink';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Tour, tourFromFirestore } from '@/types';
import TourCard from '@/components/tours/TourCard';
import AppDownloadButtons from '@/components/AppDownloadButtons';
import { useDragScroll } from '@/lib/useDragScroll';

const Newsletter = lazy(() => import('@/components/Newsletter'));
import {
  MapPin, Headphones, Globe, Smartphone, ChevronRight, Play, Search,
  Clock, Shield, Map, WifiOff, Mic, Navigation,
  Route, Download, Star, Languages, ChevronLeft, Compass
} from 'lucide-react';

interface UserLocation {
  lat: number;
  lng: number;
  city: string;
  country: string;
}

function getDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function HomePage() {
  const router = useRouter();
  const { t, locale } = useTranslation();
  const [allTours, setAllTours] = useState<Tour[]>([]);
  const [newTours, setNewTours] = useState<Tour[]>([]);
  const [nearbyTours, setNearbyTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const newToursRef = useRef<HTMLDivElement>(null);
  const nearbyToursRef = useRef<HTMLDivElement>(null);

  // Cargar tours (lazy Firebase import, limited query)
  useEffect(() => {
    const fetchTours = async () => {
      try {
        const { collection, query, where, orderBy, limit, getDocs } = await import('firebase/firestore');
        const { db } = await import('@/lib/firebase');
        const q = query(
          collection(db, 'tours'),
          where('status', '==', 'approved'),
          orderBy('createdAt', 'desc'),
          limit(24)
        );
        const snapshot = await getDocs(q);
        const tours = snapshot.docs.map(tourFromFirestore);

        setAllTours(tours);
        setNewTours(tours.slice(0, 10));

        // Defer translations to not block rendering
        if (locale !== 'es') {
          const { translateTours } = await import('@/lib/tourTranslation');
          await translateTours(tours, locale);
          setAllTours([...tours]);
          setNewTours([...tours.slice(0, 10)]);
        }
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, [locale]);

  // Geolocalización por IP (deferred — not needed for initial render)
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        if (data.latitude && data.longitude) {
          setUserLocation({
            lat: data.latitude,
            lng: data.longitude,
            city: data.city || '',
            country: data.country_name || '',
          });
        }
      } catch {
        // Geolocation not available
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Calcular tours cercanos cuando tenemos ubicación
  useEffect(() => {
    if (userLocation && allTours.length > 0) {
      const toursWithDistance = allTours
        .filter(t => t.latitude && t.longitude)
        .map(t => ({
          tour: t,
          distance: getDistanceKm(userLocation.lat, userLocation.lng, t.latitude!, t.longitude!),
        }))
        .sort((a, b) => a.distance - b.distance)
        .filter(t => t.distance < 500)
        .slice(0, 10);
      
      setNearbyTours(toursWithDistance.map(t => t.tour));
    }
  }, [userLocation, allTours]);

  // Tours filtrados por búsqueda
  const filteredTours = searchTerm.trim()
    ? allTours.filter(t => {
        const term = searchTerm.toLowerCase();
        return t.title.toLowerCase().includes(term) ||
          t.description.toLowerCase().includes(term) ||
          t.city.toLowerCase().includes(term) ||
          t.country.toLowerCase().includes(term) ||
          t.province.toLowerCase().includes(term);
      })
    : [];

  const isSearching = searchTerm.trim().length > 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Si hay resultados, ya se muestran debajo en tiempo real
    // Si el usuario pulsa enter/buscar, redirigir a /tours con más filtros
    if (searchTerm.trim()) {
      router.push(`/tours?search=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const scrollSlider = (ref: React.RefObject<HTMLDivElement | null>, direction: 'left' | 'right') => {
    if (ref.current) {
      const scrollAmount = 320;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Arrastrar con el ratón (clic + mover) para deslizar los carruseles.
  useDragScroll(newToursRef, !loading && !isSearching && newTours.length > 0);
  useDragScroll(nearbyToursRef, !loading && !isSearching && nearbyTours.length > 0);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                {t('home.heroTitle')} <span className="text-accent">{t('home.heroHighlight')}</span> {t('home.heroTitleEnd')}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/90">
                {t('home.heroSubtitle')}
              </p>
              
              {/* Buscador */}
              <form onSubmit={handleSearch} className="mt-8 relative" role="search">
                <div className="flex bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                  <div className="flex-1 flex items-center px-4">
                    <Search className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" aria-hidden="true" />
                    <label htmlFor="home-search" className="sr-only">{t('home.searchPlaceholder')}</label>
                    <input
                      id="home-search"
                      type="text"
                      placeholder={t('home.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full py-4 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 outline-none text-base bg-transparent"
                    />
                  </div>
                  <button
                    type="submit"
                    aria-label={t('common.search')}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 md:px-8 font-medium transition-colors flex items-center gap-2"
                  >
                    <span className="hidden sm:inline">{t('common.search')}</span>
                    <Search className="w-5 h-5 sm:hidden" aria-hidden="true" />
                  </button>
                </div>
              </form>

              <div className="mt-6 flex flex-wrap gap-3">
                {['Barcelona', 'Madrid', 'Roma', 'París', 'Londres'].map(city => (
                  <Link
                    key={city}
                    href={`/tours?search=${city}`}
                    className="px-4 py-2 bg-white/15 hover:bg-white/25 rounded-full text-sm backdrop-blur-sm transition-colors"
                  >
                    <MapPin className="w-3 h-3 inline mr-1" />{city}
                  </Link>
                ))}
              </div>

              <div className="mt-10 grid grid-cols-3 gap-8">
                <div><div className="text-3xl font-bold">20+</div><div className="text-white/90 text-sm">{t('home.languages')}</div></div>
                <div><div className="text-3xl font-bold">GPS</div><div className="text-white/90 text-sm">{t('home.automatic')}</div></div>
                <div><div className="text-3xl font-bold">100%</div><div className="text-white/90 text-sm">{t('home.offline')}</div></div>
              </div>

              {/* Botones de descarga (App Store + Google Play) */}
              <div className="mt-10">
                <AppDownloadButtons />
              </div>
            </div>
            
            <div className="hidden md:flex justify-center">
              <div className="relative">
                <div className="w-72 h-[500px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                  <div className="w-full h-full bg-gradient-to-br from-primary-100 to-secondary-100 rounded-[2.5rem] flex items-center justify-center">
                    <div className="text-center p-6">
                      <div className="mx-auto flex items-center justify-center mb-4">
                        <Image src="/logo_icono.webp" alt="IteroGO" width={80} height={80} />
                      </div>
                      {/* Texto sobre la "pantalla" del móvil: el degradado
                          from-primary-100/to-secondary-100 es siempre claro,
                          así que el texto NO debe llevar variante dark. */}
                      <h3 className="text-gray-800 font-semibold text-lg">IteroGO App</h3>
                      <p className="text-gray-600 text-sm mt-2">{t('home.appAvailable')}</p>
                      <Play className="w-12 h-12 text-primary mx-auto mt-6" />
                    </div>
                  </div>
                </div>
                <div className="absolute -left-12 top-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 w-48">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-8 h-8 text-primary" />
                    <div><div className="font-semibold text-gray-900 dark:text-gray-100">{t('home.gpsActive')}</div><div className="text-xs text-gray-500 dark:text-gray-400">{t('home.autoAudio')}</div></div>
                  </div>
                </div>
                <div className="absolute -right-8 bottom-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 w-44">
                  <div className="flex items-center gap-3">
                    <Globe className="w-8 h-8 text-secondary" />
                    <div><div className="font-semibold text-gray-900 dark:text-gray-100">{t('home.languagesCount')}</div><div className="text-xs text-gray-500 dark:text-gray-400">{t('home.autoTranslation')}</div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 block -mb-px">
          <svg className="w-full h-auto display-block" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none"><path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" className="fill-white dark:fill-gray-900"/></svg>
        </div>
      </section>

      {/* Resultados de búsqueda */}
      {isSearching && !loading && (
        <section className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {t('home.searchResultsFor')} &quot;{searchTerm}&quot;
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-1">{filteredTours.length} {filteredTours.length === 1 ? t('home.tourFound') : t('home.toursFound')}</p>
              </div>
              <button
                onClick={() => setSearchTerm('')}
                className="text-primary font-medium hover:underline"
              >
                {t('home.clearSearch')}
              </button>
            </div>
            
            {filteredTours.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTours.map((tour) => <TourCard key={tour.id} tour={tour} />)}
              </div>
            ) : (
              <div className="text-center py-12">
                <Map className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 dark:text-gray-400 text-lg">{t('home.noToursFor')} &quot;{searchTerm}&quot;</p>
                <p className="text-gray-400 dark:text-gray-500 mt-2">{t('home.tryAnotherCity')}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Nuevos Tours - Slider horizontal */}
      {!loading && !isSearching && newTours.length > 0 && (
        <section className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">🆕 {t('home.newTours')}</h2>
                <p className="text-gray-500 dark:text-gray-400 mt-1">{t('home.newToursSubtitle')}</p>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={() => scrollSlider(newToursRef, 'left')}
                  aria-label={t('common.previous')}
                  className="p-2 rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" aria-hidden="true" />
                </button>
                <button
                  onClick={() => scrollSlider(newToursRef, 'right')}
                  aria-label={t('common.next')}
                  className="p-2 rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" aria-hidden="true" />
                </button>
              </div>
            </div>
            
            <div
              ref={newToursRef}
              className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {newTours.map((tour) => (
                <div key={tour.id} className="flex-shrink-0 w-[280px] sm:w-[300px]" style={{ scrollSnapAlign: 'start' }}>
                  <TourCard tour={tour} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tours cerca de ti */}
      {!loading && !isSearching && nearbyTours.length > 0 && (
        <section className="py-16 bg-gray-50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                  📍 {t('home.nearbyTours')}
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                  {userLocation ? t('home.nearbyToursSubtitle', { city: userLocation.city, country: userLocation.country }) : t('home.nearbyToursGeneric')}
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={() => scrollSlider(nearbyToursRef, 'left')}
                  aria-label={t('common.previous')}
                  className="p-2 rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" aria-hidden="true" />
                </button>
                <button
                  onClick={() => scrollSlider(nearbyToursRef, 'right')}
                  aria-label={t('common.next')}
                  className="p-2 rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" aria-hidden="true" />
                </button>
              </div>
            </div>
            
            <div
              ref={nearbyToursRef}
              className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {nearbyTours.map((tour) => (
                <div key={tour.id} className="flex-shrink-0 w-[280px] sm:w-[300px]" style={{ scrollSnapAlign: 'start' }}>
                  <TourCard tour={tour} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Todos los tours */}
      {!isSearching && (
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">🌍 {t('home.allTours')}</h2>
              <p className="text-gray-500 dark:text-gray-400 mt-1">{t('home.allToursSubtitle')}</p>
            </div>
            <Link href="/tours" className="hidden md:flex items-center text-primary font-medium hover:underline">
              {t('common.seeAll')} <ChevronRight className="w-5 h-5 ml-1" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="card animate-pulse"><div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-t-xl" /><div className="p-4 space-y-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" /><div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4" /></div></div>
              ))}
            </div>
          ) : allTours.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allTours.slice(0, 12).map((tour) => <TourCard key={tour.id} tour={tour} />)}
              </div>
              {allTours.length > 12 && (
                <div className="text-center mt-10">
                  <Link href="/tours" className="btn-primary inline-flex items-center">
                    {t('home.seeAllTours')} ({allTours.length}) <ChevronRight className="ml-2 w-5 h-5" />
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <Map className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 dark:text-gray-400 text-lg">{t('home.comingSoon')}</p>
              <p className="text-gray-400 dark:text-gray-500 mt-2">{t('home.beFirstGuide')}</p>
              <Link href="/guias" className="mt-4 btn-primary inline-flex items-center">
                {t('home.createFirstTour')} <ChevronRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Features Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 dark:text-gray-100">{t('home.whyIteroGO')}</h2>
          <p className="text-gray-500 dark:text-gray-400 text-center max-w-2xl mx-auto mb-12">{t('home.whySubtitle')}</p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-primary-100 dark:bg-gray-700 rounded-xl flex items-center justify-center mb-6">
                <Navigation className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 dark:text-gray-100">{t('home.featureGPS')}</h3>
              <p className="text-gray-600 dark:text-gray-300">{t('home.featureGPSDesc')}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-secondary-100 dark:bg-gray-700 rounded-xl flex items-center justify-center mb-6">
                <WifiOff className="w-7 h-7 text-secondary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 dark:text-gray-100">{t('home.featureOffline')}</h3>
              <p className="text-gray-600 dark:text-gray-300">{t('home.featureOfflineDesc')}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-6">
                <Languages className="w-7 h-7 text-accent" />
              </div>
              <h3 className="text-xl font-semibold mb-2 dark:text-gray-100">{t('home.featureLanguages')}</h3>
              <p className="text-gray-600 dark:text-gray-300">{t('home.featureLanguagesDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 dark:text-gray-100">{t('home.howItWorks')}</h2>
          <p className="text-gray-500 dark:text-gray-400 text-center mb-12">{t('home.howItWorksSubtitle')}</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2 dark:text-gray-100">{t('home.step1Title')}</h3>
              <p className="text-gray-600 dark:text-gray-300">{t('home.step1Desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-secondary-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="font-semibold text-lg mb-2 dark:text-gray-100">{t('home.step2Title')}</h3>
              <p className="text-gray-600 dark:text-gray-300">{t('home.step2Desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Headphones className="w-8 h-8 text-accent" />
              </div>
              <h3 className="font-semibold text-lg mb-2 dark:text-gray-100">{t('home.step3Title')}</h3>
              <p className="text-gray-600 dark:text-gray-300">{t('home.step3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Más funciones */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 dark:text-gray-100">{t('home.allFeatures')}</h2>
          <p className="text-gray-500 dark:text-gray-400 text-center max-w-2xl mx-auto mb-12">{t('home.allFeaturesSubtitle')}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Route, title: t('home.featureRoute'), desc: t('home.featureRouteDesc') },
              { icon: WifiOff, title: t('home.featureNoInternet'), desc: t('home.featureNoInternetDesc') },
              { icon: Star, title: t('home.featureReviews'), desc: t('home.featureReviewsDesc') },
              { icon: Mic, title: t('home.featureAudio'), desc: t('home.featureAudioDesc') },
              { icon: Clock, title: t('home.featureRhythm'), desc: t('home.featureRhythmDesc') },
              { icon: Shield, title: t('home.featureQuality'), desc: t('home.featureQualityDesc') },
              { icon: Smartphone, title: t('home.featurePlatforms'), desc: t('home.featurePlatformsDesc') },
              { icon: Compass, title: t('home.featureCities'), desc: t('home.featureCitiesDesc') },
            ].map((feature, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <feature.icon className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold mb-1 dark:text-gray-100">{feature.title}</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Guías */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('home.ctaGuideTitle')}</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {t('home.ctaGuideSubtitle')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
            <div><div className="text-3xl font-bold">60%</div><div className="text-white/90 text-sm">{t('home.commission')}</div></div>
            <div><div className="text-3xl font-bold">100%</div><div className="text-white/90 text-sm">{t('home.firstMonths')}</div></div>
            <div><div className="text-3xl font-bold">📱</div><div className="text-white/90 text-sm">{t('home.createFromMobile')}</div></div>
            <div><div className="text-3xl font-bold">🌍</div><div className="text-white/90 text-sm">{t('home.globalReach')}</div></div>
          </div>
          <Link href="/guias" className="btn-accent inline-flex items-center text-lg px-8 py-4">
            {t('home.startCreating')} <ChevronRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Newsletter */}
      <Suspense fallback={null}>
        <Newsletter />
      </Suspense>

      {/* Download App */}
      <section id="descargar" className="scroll-mt-24 py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 dark:text-gray-100">{t('home.downloadApp')}</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">{t('home.downloadAppSubtitle')}</p>
          <AppDownloadButtons className="justify-center" />
        </div>
      </section>
    </div>
  );
}
